import { api } from 'API'
import { TeamListItem, TeamMembershipFilter } from 'types/auth'

/**
 * GET /users/me/teams — the Teams management list. Separate from the auth-focused
 * `getMyMemberships` so that hot path stays lean. Optional server-side membership
 * filter (owned / invited / invite-pending). `extraParams` carries the advanced
 * filter query params (statuses/sizes/activity/sports/seasons/sort) — sent only
 * once TEAMS_BE_FILTER_ENABLED is flipped on; unknown params are otherwise omitted.
 */
export async function getMyTeams(
  membership?: TeamMembershipFilter,
  extraParams?: Record<string, string>
): Promise<TeamListItem[]> {
  const response = await api.get('/users/me/teams', {
    params: { ...(membership ? { membership } : {}), ...(extraParams ?? {}) }
  })
  return response.data as TeamListItem[]
}

/**
 * POST /teams/{teamId}/archive — hides the seasonal Team from default lists while
 * preserving its roster, coach assignments, events and communication history.
 * Archiving is never a delete.
 *
 * Distinct from archiving the Organization (`POST /organizations/{orgId}/archive`),
 * which retires the whole account. The two were the same call while an
 * Organization and a Team were one unit.
 */
export async function archiveTeam(teamId: number): Promise<void> {
  await api.post(`/teams/${teamId}/archive`)
}

/** POST /teams/{teamId}/restore — brings an archived Team back into active views. */
export async function restoreTeam(teamId: number): Promise<void> {
  await api.post(`/teams/${teamId}/restore`)
}

/** Payload for creating a seasonal Team inside the active Organization. */
export interface ICreateTeamPayload {
  name: string
  sports?: string[]
  sport?: string
  season?: string
  year?: number
  ageLevel?: string
  levelOfPlay?: string
  description?: string
  primaryLocation?: string
  image?: string
}

/** POST /teams — creates a seasonal Team under the active Organization. */
export async function createTeam(payload: ICreateTeamPayload): Promise<{ id: number }> {
  const response = await api.post('/teams', payload)
  return response.data as { id: number }
}

/** PATCH /teams/{teamId} — updates the seasonal Team's own fields. */
export async function updateTeam(teamId: number, payload: Partial<ICreateTeamPayload>): Promise<void> {
  await api.patch(`/teams/${teamId}`, payload)
}

export type TeamListSortBy = 'updated' | 'name-asc' | 'name-desc' | 'roster-desc'

export interface ITeamListParams {
  /**
   * Scope the request to one organization explicitly. Without it the interceptor
   * stamps the active org, which is wrong for a cross-org list: the caller wants
   * the Teams of a named organization, not of whichever one is current.
   */
  orgId?: number
  page?: number
  pageSize?: number
  /** Matches the team name; the server owns the match. */
  keyword?: string
  statuses?: string[]
  seasons?: string[]
  ageLevels?: string[]
  levelsOfPlay?: string[]
  sortBy?: TeamListSortBy
  includeArchived?: boolean
  /** Every match in one response — for pickers that render a dropdown, not a table. */
  noPagination?: boolean
}

export interface ITeamListResponse {
  results: ITeam[]
  totalCount: number
  /** Distinct sports across every match, not just this page — for the org banner. */
  sports: string[]
}

/**
 * GET /teams — one page of the seasonal Teams in the active Organization.
 *
 * Org scope rides on the interceptor's `?orgId=` stamp; the endpoint resolves it
 * server-side and never trusts a body value. Search, filter, sort and paging are
 * query params: the management list holds one page, and each row's `rosterCount`
 * is computed server-side rather than by counting the org's whole member list.
 *
 * Arrays go as CSV rather than repeated keys, matching the other list endpoints.
 */
export async function getTeams(params: ITeamListParams = {}): Promise<ITeamListResponse> {
  const response = await api.get('/teams', {
    params: {
      orgId: params.orgId,
      page: params.page,
      pageSize: params.pageSize,
      keyword: params.keyword?.trim() || undefined,
      statuses: params.statuses?.length ? params.statuses.join(',') : undefined,
      seasons: params.seasons?.length ? params.seasons.join(',') : undefined,
      ageLevels: params.ageLevels?.length ? params.ageLevels.join(',') : undefined,
      levelsOfPlay: params.levelsOfPlay?.length ? params.levelsOfPlay.join(',') : undefined,
      sortBy: params.sortBy,
      includeArchived: params.includeArchived ? true : undefined,
      noPagination: params.noPagination ? true : undefined
    }
  })
  return response.data as ITeamListResponse
}

/** Shape returned by the Team endpoints. */
export interface ITeam {
  id: number
  organizationId: number
  name: string
  sports?: string[]
  sport?: string
  season?: string
  year?: number
  ageLevel?: string
  levelOfPlay?: string
  description?: string
  primaryLocation?: string
  image?: string
  status?: string
  /** Set by the API on every write; drives the list's default "Last updated" sort. */
  updatedAt?: string
  createdAt?: string
  archivedAt?: string | null
}

/** GET /teams/{teamId} — one seasonal Team. */
export async function getTeamById(teamId: number | string): Promise<ITeam> {
  const response = await api.get(`/teams/${teamId}`)
  return response.data as ITeam
}
